<template>
  <v-dialog
    :model-value="show"
    :max-width="maxWidth"
    :persistent="persistent"
    scrollable
    @update:model-value="$emit('update:show', $event)"
  >
    <v-card :class="['base-modal-card', cardClass]" :style="{ minWidth: minWidth + 'px' }">
      <!-- Header -->
      <v-card-title :class="headerClass" :style="headerStyle">
        <div class="d-flex align-center" style="gap: 12px">
          <!-- Optional icon -->
          <v-icon v-if="icon" :color="iconColor" :size="iconSize">
            {{ icon }}
          </v-icon>
          <span>{{ title }}</span>
        </div>
        <v-icon v-if="showClose" @click="handleClose" color="black" size="28" style="cursor: pointer">
          mdi-close
        </v-icon>
      </v-card-title>

      <!-- Content -->
      <v-card-text :class="contentClass" :style="contentStyle">
        <slot />
      </v-card-text>

      <!-- Optional Footer -->
      <v-card-actions v-if="$slots.footer" :class="footerClass">
        <slot name="footer" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'BaseModal',

  props: {
    show: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      default: ''
    },
    iconColor: {
      type: String,
      default: '#FFC627'
    },
    iconSize: {
      type: [String, Number],
      default: 24
    },
    maxWidth: {
      type: [String, Number],
      default: '720'
    },
    minWidth: {
      type: [String, Number],
      default: '460'
    },
    persistent: {
      type: Boolean,
      default: false
    },
    showClose: {
      type: Boolean,
      default: true
    },
    headerStyle: {
      type: String,
      default: 'background-color: #fec627; font-size: 24px; line-height: 1.5;'
    },
    headerClass: {
      type: String,
      default: 'font-weight-bold px-6 px-md-12 py-4 py-md-6 d-flex align-center justify-space-between'
    },
    contentClass: {
      type: String,
      default: 'px-6 py-8 px-md-12 py-md-12'
    },
    contentStyle: {
      type: String,
      default: ''
    },
    footerClass: {
      type: String,
      default: 'px-6 pb-8 px-md-12 pb-md-12'
    },
    cardClass: {
      type: String,
      default: 'rounded-lg'
    }
  },

  emits: ['update:show', 'close'],

  methods: {
    handleClose() {
      this.$emit('close');
      this.$emit('update:show', false);
    }
  }
});
</script>

<style scoped>
/* Let the header title wrap instead of clipping — Vuetify defaults v-card-title
   to `white-space: nowrap`, which truncates long titles once the card is
   constrained to a narrow viewport. */
.base-modal-card :deep(.v-card-title) {
  white-space: normal;
}

/* On phones the fixed min-width would force the dialog wider than the viewport
   (horizontal overflow, clipped CTA). Let the card shrink to fit below sm, and
   ease the header font down so long titles fit. */
@media (max-width: 599.98px) {
  .base-modal-card {
    min-width: 0 !important;
  }

  .base-modal-card :deep(.v-card-title) {
    font-size: 20px !important;
    line-height: 1.3 !important;
  }
}
</style>
